import { nextTick } from "vue";
import { defineStore } from "pinia";
import * as Sentry from "@sentry/vue";
import type { Components } from "@/assets/docs/client.d";
import { KnownUserCompanyIds, WellKnownPartyIds } from "@/components/product/Types";
import { isEntraAuthActive } from "@/auth/entraAuthService";

export interface User {
  id?: string | null;
  userCompanyId?: number | null; // int32
  firstName?: string | null;
  lastName?: string | null;
  userName?: string | null;
  email?: string | null;
  currentRole?: string | null;
  isAdmin: boolean;
  isLegal: boolean;
  isOperations: boolean;
  isLightUser: boolean;
  canSeeAllFlows: boolean;
  hasGenTwoProAccess: boolean;
  hasDocumentHubAccess: boolean;
  isUserPayingAgent: boolean;
  isUserAssetManager: boolean;
  isUserIssuer: boolean;
  isUserGentwo: boolean;
  isClient: boolean;
  userCompanyName: string;
  isImpersonating: boolean;
  srfAccess: boolean;
  srfLiteLevelAccess?: string;
  amcForPmsLevelAccess?: string;
  canAccessAssetizationUniverse: boolean;
  isISP: boolean;
  isAlbecq: boolean;
  isCSC: boolean;
  hasDrPricingAccessClaim: boolean;
  isOrderAcceptancePayingAgent: boolean;
  canSeeBatchPayments: boolean;
  impersonatedOriginalUser: string;
  theme: string;
  canImpersonate: boolean;
  availableFunctions: AvailableFunction[];
  activeFunctionEmail?: string | null;
  multiFunctionSourceUser?: string | null;
  platformUserTermsAcceptanceRequired: boolean;
}

export interface AvailableFunction {
  id?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  isSelf?: boolean;
}

export interface AccountState {
  user: User;
  userLogged: boolean;
  manager?: Components.Schemas.G2fpModelsViewModelsAppWebProductHubUserVm;
  company?: Components.Schemas.G2fpModelsViewModelsAppWebProductHubCompanyVm;
  issuers?: Components.Schemas.G2fpModelsViewModelsAppWebProductHubIssuerVm[] | null;
  webhookList: [];
}

export const useAccountStore = defineStore("AccountStore", {
  state: (): AccountState => ({
    user: {} as User,
    userLogged: false,
    manager: {},
    company: {},
    issuers: [],
    webhookList: [],
  }),
  getters: {
    // True only when the session is acting as a linked function other than the user's own account.
    isActingAsOtherFunction(state): boolean {
      const active = state.user?.activeFunctionEmail;
      if (!active) return false;

      const match = state.user?.availableFunctions?.find(f => f.email === active);
      return match ? !match.isSelf : active !== state.user?.multiFunctionSourceUser;
    },
  },
  actions: {
    async init() {
      try {
        const whoAmIResponse = await this.apiClient.WhoAmI();
        const currentUserName = whoAmIResponse?.data?.currentUserName;

        if (currentUserName && currentUserName !== "<anonymous>") {
          const loginResponse = await this.apiClient.GetCurrentUserDetail();

          this.setUser(loginResponse.data);

          Sentry.setUser({
            email: loginResponse.data.email!,
            userName: loginResponse.data.userName,
            id: loginResponse.data.id!,
          });
          await this.fetchCsrfToken();
        }
      } catch (error) {
        // Under the Entra flow a rejected whoami/me means the bearer token was
        // accepted by MSAL but not resolved to an app-web user (e.g. not
        // provisioned). Stay unauthenticated so bootstrap completes and the guard
        // routes to access-denied, instead of crashing bootstrap into a redirect loop.
        if (isEntraAuthActive()) {
          return;
        }
        throw error;
      }
    },
    setUser(
      userInfo: Components.Schemas.G2fpAppWebApplicationFeaturesAccountGetUserDetailUserDetailDto,
    ) {
      if (userInfo.email) {
        Sentry.setUser({ email: userInfo.email });
      }

      this.user = {
        id: userInfo.id,
        userCompanyId: userInfo.userCompanyId,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        userName: userInfo.userName,
        email: userInfo.email,
        // TODO: get proper Role
        currentRole: userInfo.roles && userInfo.roles[0].replace("Role", ""),
      } as User;
      this.userLogged = userInfo?.id !== undefined;

      if (this.user && userInfo && this.userLogged) {
        this.user.isAdmin = !!userInfo.roles?.includes("AdminRole");
        this.user.isLegal = !!userInfo.roles?.includes("LegalRole");
        this.user.isOperations = !!userInfo.roles?.includes("OperationsRole");
        this.user.isLightUser = !!userInfo.roles?.includes("LightUserRole");
        this.user.canSeeAllFlows = helpers.checkClaim(userInfo, "CanManageAllFlowsClaim");
        this.user.hasGenTwoProAccess = helpers.checkClaim(userInfo, "CanManageOwnFlowClaim");
        this.user.hasDocumentHubAccess = helpers.checkClaim(userInfo, "SqlAccessClaim");
        this.user.isUserPayingAgent = helpers.checkClaim(userInfo, "IsPayingAgentClaim");
        this.user.isUserAssetManager = helpers.checkClaim(userInfo, "IsAssetManagerClaim");
        this.user.isUserIssuer = helpers.checkClaim(userInfo, "IsIssuerClaim");
        this.user.isUserGentwo = helpers.checkClaim(userInfo, "IsInternalClaim");
        this.user.isImpersonating = helpers.checkClaim(userInfo, "IsImpersonating");
        this.user.srfAccess = helpers.checkClaim(userInfo, "SrfAccessClaim");
        this.user.srfLiteLevelAccess = helpers.getClaim(userInfo, "SrfLiteAccessLevelClaim");
        this.user.amcForPmsLevelAccess = helpers.getClaim(userInfo, "AmcForPmsAccessLevelClaim");
        this.user.canAccessAssetizationUniverse = helpers.checkClaim(userInfo, "CanAccessAssetizationUniverseClaim");
        this.user.hasDrPricingAccessClaim = helpers.checkClaim(userInfo, "DrPricingAccessClaim");
        this.user.isCSC = userInfo.userCompanyId == KnownUserCompanyIds.CSC;
        this.user.isISP =
          userInfo.groups?.find(g => g.name?.includes("group::ISP")) !== undefined ||
          userInfo.userCompanyId === KnownUserCompanyIds.ISP ||
          userInfo.userCompanyId === WellKnownPartyIds.IspAsPS;

        this.user.isClient = !!helpers.getClaim(userInfo, "CompanyIdClaim");
        this.user.userCompanyName = helpers.getClaim(userInfo, "UserCompanyNameClaim");
        this.user.isAlbecq = this.user.userCompanyId === KnownUserCompanyIds.Albecq;
        this.user.isOrderAcceptancePayingAgent =
          this.user.isUserGentwo || this.user.isUserPayingAgent;
        this.user.canSeeBatchPayments =
          this.user.isAdmin ||
          this.user.isUserGentwo ||
          (this.user.isUserPayingAgent && this.user.isISP) ||
          this.user.userCompanyId === KnownUserCompanyIds.Albecq ||
          this.user.isCSC;

        this.user.canImpersonate = helpers.checkClaim(userInfo, "CanImpersonateClaim");

        if (this.user.isImpersonating) {
          this.user.impersonatedOriginalUser = helpers.getClaim(userInfo, "OriginalUser");
        }

        this.user.availableFunctions = userInfo.availableFunctions ?? [];
        this.user.activeFunctionEmail = userInfo.activeFunctionEmail;
        this.user.multiFunctionSourceUser = helpers.getClaim(userInfo, "MultiFunctionSourceUser");
        this.user.platformUserTermsAcceptanceRequired = !!userInfo.platformUserTermsAcceptanceRequired;
      } else {
        nextTick(() => sessionStorage.clear());
      }

      const savedMode = this.user?.theme || localStorage.getItem("userTheme") || "system";
      this.setUserTheme(savedMode);
    },
    setUserTheme(userTheme: string) {
      if (this.user) {
        this.user.theme = userTheme;
      }
      localStorage.setItem("userTheme", userTheme);
      if (userTheme === "system") {
        this.applySystemTheme();
      } else {
        this.removeSystemThemeListener();
        this.toggleDarkClass(userTheme === "dark");
      }
    },
    toggleTheme() {
      const newTheme = this.user?.theme === "light" ? "dark" : "light";
      this.setUserTheme(newTheme);
    },
    applySystemTheme() {
      const isDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
      this.toggleDarkClass(isDarkMode);
      window
        .matchMedia("(prefers-color-scheme: dark)")
        .addEventListener("change", this.handleSystemThemeChange);
    },
    removeSystemThemeListener() {
      window
        .matchMedia("(prefers-color-scheme: dark)")
        .removeEventListener("change", this.handleSystemThemeChange);
    },
    handleSystemThemeChange(e) {
      this.toggleDarkClass(e.matches);
    },
    toggleDarkClass(isDark) {
      document.documentElement.classList.toggle("dark", isDark);
    },
    async fetchCsrfToken() {
      try {
        await this.apiClient.get("/apptwo/antiforgery/token", { withCredentials: true });
      } catch (error) {
        console.error("Error fetching CSRF token:", error);
        return null;
      }
    },
    async forgotPassword(credentials) {
      const response = await this.apiClient.ForgotPassword(null, credentials);
      return response;
    },
    async resetPassword(credentials) {
      const resetPasswordResponse = await this.apiClient.ResetPassword(
        null,
        JSON.stringify(credentials),
      );
      if (resetPasswordResponse) {
        this.setUser(resetPasswordResponse.data);
      }
      return resetPasswordResponse;
    },
    async impersonateUser(credentials) {
      await this.apiClient.StartUserImpersonation(null, credentials);
    },
    async stopImpersonateUser() {
      await this.apiClient.StopUserImpersonation(null);
    },
    async switchFunction(targetUserId: string) {
      await this.apiClient.SwitchFunction(null, { targetUserId });
    },
    async loadCompanyInfo(force) {
      if (!this.company?.id || force) {
        const response = await this.apiClient.GetCompanyDetails();

        this.company = response.data.company;
        this.issuers = response.data.issuers;
        this.manager = response.data.gentwoResponsibleUser;
      }
    },

    async confirmEmail(payload) {
      const response = await this.apiClient.ConfirmEmail(null, payload);
      return response;
    },

    //Dev settings
    async testWebhook(data) {
      return await this.apiClient.TestWebhook(null, JSON.stringify(data));
    },

    async saveWebhook(data) {
      return await this.apiClient.SaveWebhook(null, JSON.stringify(data));
    },
    async getWebhooks() {
      const response = await this.apiClient.GetWebhooks(null);
      this.webhookList = response.data;
      return response;
    },
    async deleteWebhook(guid) {
      return await this.apiClient.DeleteWebhook(guid);
    },
    getCurrentUserSessionId(): string | null {
      if (!this.user?.id) {
        console.error("User ID is not available.");
        return null;
      }
      return `user_${this.user.id}`;
    },
  },
});

const helpers = {
  checkClaim(user, claimType, claimValue = "True") {
    const claim = user.claims.find(claim => claim.type === claimType);
    return claim && claimValue && claim.value === claimValue;
  },
  getClaim(user, claimType) {
    const claim = user.claims.find(claim => claim.type === claimType);
    return claim?.value;
  },
};
